import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  Box,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import _ from "lodash";
import * as actions from "../Common/actions";
import { toastError, toastSuccess, checkUserDetailsSession, setUserDetailsSession, clearUserDetailsSession } from "../../utils";
import Pagination from "@mui/lab/Pagination";
import TextField from "@mui/material/TextField";
import CustomShowModal from "../Common/customShowModal";
import PayoutPasswordModal from './PayoutPasswordModal';

const CssTextField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  color: "#fff",
  width: "100%",
  "& label": {
    fontFamily: "'DM Sans', sans-serif",
    color: "white",
  },
  "& label.Mui-focused": {
    color: "white",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "red",
  },
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused fieldset": {
      borderColor: "#10baa8",
      borderWidth: "1px",
    },
  },
});

// Helper function to calculate time ago with detailed breakdown
const getTimeAgo = (dateString) => {
  if (!dateString) return "-";

  const updatedDate = new Date(dateString);
  const now = new Date();

  let years = now.getFullYear() - updatedDate.getFullYear();
  let months = now.getMonth() - updatedDate.getMonth();
  let days = now.getDate() - updatedDate.getDate();

  // Adjust for negative days
  if (days < 0) {
    months--;
    const lastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    days += lastMonth.getDate();
  }

  // Adjust for negative months
  if (months < 0) {
    years--;
    months += 12;
  }

  // Build the result string
  const parts = [];

  if (years > 0) {
    parts.push(`${years} year${years > 1 ? 's' : ''}`);
  }
  if (months > 0) {
    parts.push(`${months} month${months > 1 ? 's' : ''}`);
  }
  if (days > 0) {
    parts.push(`${days} day${days > 1 ? 's' : ''}`);
  }

  if (parts.length === 0) {
    return "Today";
  }

  return parts.join(', ');
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

function Payout(props) {
  const [payouts, setPayouts] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState({
    user_id: "",
    request_id: "",
    userEmail: "",
    utr: "",
    ref: "",
    gross_amt: "",
    tds: "",
    amt: "",
  });
  const [withdrawOld, setWithdrawOld] = useState([]);
  const [count, setCount] = useState(0);
  const [filters, setFilters] = useState({
    limit: 50,
    offset: 0,
    page: 1,
    search: "",
  });
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const sessionValid = checkUserDetailsSession();
    if (sessionValid) {
      setIsAuthenticated(true);
      getAllPayouts();
      onGetWithdrawCOld(filters);
    } else {
      setShowPasswordModal(true);
      clearUserDetailsSession();
    }
  }, []);

  // Periodic session validation every 5 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const intervalId = setInterval(() => {
      const sessionValid = checkUserDetailsSession();
      if (!sessionValid) {
        setIsAuthenticated(false);
        setShowPasswordModal(true);
        setPasswordError('Your session has expired. Please re-enter the password.');
      }
    }, 300000); // 5 minutes

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  // Check session when tab becomes visible again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        const sessionValid = checkUserDetailsSession();
        if (!sessionValid) {
          setIsAuthenticated(false);
          setShowPasswordModal(true);
          setPasswordError('Your session has expired. Please re-enter the password.');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  const getAllPayouts = () => {
    props.setLoader(true);
    actions
      .getWithdrawRequestAdmin()
      .then((res) => {
        props.setLoader(false);
        setPayouts(res.body);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load payouts');
      });
  };

  const onClickSettleAndEmail = () => {
    if (!selectedItem.utr || !selectedItem.ref || !selectedItem.gross_amt || !selectedItem.tds || !selectedItem.amt) {
      toastError("All fields are required");
      return;
    }
    if (selectedItem.utr && selectedItem.ref && selectedItem.gross_amt && selectedItem.tds && selectedItem.amt) {
      onConfirmSettle();
    }
  };

  const onClickSettle = (item) => {
    setSelectedItem({
      user_id: item.user_id,
      request_id: item.request_id,
      userEmail: item.email,
      utr: "",
      ref: "",
      gross_amt: "",
      tds: "",
      amt: "",
    });
    setConfirmOpen(true);
  };

  const onConfirmSettle = () => {
    props.setLoader(true);
    actions
      .sentMoneyByAdmin(selectedItem)
      .then((res) => {
        setConfirmOpen(false);
        getAllPayouts();
        onGetWithdrawCOld(filters);
        toastSuccess(res.message);
        props.setLoader(false);
      })
      .catch((err) => {
        setConfirmOpen(false);
        props.setLoader(false);
        toastError(err?.message || 'Failed to settle payout');
      });
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetWithdrawCOld(newFilters);
  };

  const onGetWithdrawCOld = (filters) => {
    props.setLoader(true);
    actions
      .getWithdrawHistoryAdmin({ ...filters })
      .then((res) => {
        props.setLoader(false);
        setWithdrawOld(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || 'Failed to load history');
      });
  };

  const delaySearch = useCallback(
    _.debounce((filters) => onGetWithdrawCOld(filters), 500),
    []
  );

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const handlePasswordVerification = (password) => {
    setPasswordLoading(true);
    setPasswordError('');

    actions.verifyUserDetailsPassword({ password })
      .then((res) => {
        setPasswordLoading(false);
        setShowPasswordModal(false);
        setIsAuthenticated(true);
        setUserDetailsSession(Date.now());
        getAllPayouts(); // Load data after authentication
        onGetWithdrawCOld(filters);
        toastSuccess('Access granted');
      })
      .catch((err) => {
        setPasswordLoading(false);
        setPasswordError(err?.message || 'Invalid password. Please try again.');
      });
  };


  // Don't render content until authenticated
  if (!isAuthenticated) {
    return (
      <PayoutPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
    );
  }

  return (
    <>
      <PayoutPasswordModal
        open={showPasswordModal}
        onPasswordVerified={handlePasswordVerification}
        loading={passwordLoading}
        error={passwordError}
      />
      <Grid container>
        <Grid item xs={12}>
          <p style={{ fontWeight: 500 }}>Review Required</p>

        <Box mt={2}>
          <CssTextField
            type="text"
            variant="outlined"
            size="small"
            onChange={onChangeSearch}
            value={filters.search}
            placeholder="Search..."
          />
        </Box>
      </Grid>
      <Grid item xs={12} style={{ marginTop: "10px", marginBottom: "10px" }}>
        <Box>
          <TableContainer component={Paper}>
            <Table className="universal-table">
              <TableHead>
                <TableRow
                  style={{
                    background:
                      "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
                  }}
                >
                  <TableCell className="color-white">No.</TableCell>
                  <TableCell className="color-white">Name</TableCell>
                  <TableCell className="color-white">Email</TableCell>
                  <TableCell className="color-white">Phone</TableCell>
                  <TableCell className="color-white">Revenue</TableCell>
                  <TableCell className="color-white">Bank Updated On</TableCell>
                  <TableCell className="color-white">Ago</TableCell>
                  <TableCell className="color-white">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payouts.length ? (
                  payouts.map((item, key) => {
                    return (
                      <TableRow key={key}>
                        <TableCell>{key + 1}</TableCell>
                        <TableCell>{item.name || "-"}</TableCell>
                        <TableCell>{item.email || "-"}</TableCell>
                        <TableCell>{item.phone || "-"}</TableCell>
                        <TableCell style={{ fontWeight: "bold" }}>{item.revenue || "-"}</TableCell>
                        <TableCell>{formatDate(item.bank_updated_on)}</TableCell>
                        <TableCell style={{ color: "red" }}>{getTimeAgo(item.bank_updated_on)}</TableCell>
                        <TableCell>
                          <button
                            className="settle-payout"
                            onClick={() => onClickSettle(item)}
                          >
                            SETTLE
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan="8" align="center">
                      No Request Found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Grid>

      <Grid item xs={12} style={{ marginTop: "20px" }}>
        <h2
          style={{
            fontWeight: 500,
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          Payout History
        </h2>
        <TableContainer component={Paper}>
          <Table className="universal-table">
            <TableHead>
              <TableRow
                style={{
                  background:
                    "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
                }}
              >
                <TableCell className="color-white">User ID</TableCell>
                <TableCell className="color-white">Name</TableCell>
                <TableCell className="color-white">Email</TableCell>
                <TableCell className="color-white">UTR</TableCell>
                <TableCell className="color-white">REF</TableCell>
                <TableCell className="color-white">Amount ($)</TableCell>
                <TableCell className="color-white">Gross Amount (INR)</TableCell>
                <TableCell className="color-white">TDS Amount</TableCell>
                <TableCell className="color-white">Net Payable Amount</TableCell>
                <TableCell className="color-white">Settled On</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawOld.map((item, index) => {
                return (
                  <TableRow key={index}>
                    <TableCell>{item.user_id}</TableCell>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.email}</TableCell>
                    <TableCell>{item.utr}</TableCell>
                    <TableCell>{item.ref}</TableCell>
                    <TableCell>{item.amount}</TableCell>
                    <TableCell>{item.gross_amt || "-"}</TableCell>
                    <TableCell>{item.tds || "-"}</TableCell>
                    <TableCell>{item.email_amt}</TableCell>
                    <TableCell>{item.settled_on}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Pagination
            count={totalPages}
            showFirstButton
            showLastButton
            onChange={onPageChange}
            page={filters.page || 0}
          />
        </TableContainer>
      </Grid>

      <CustomShowModal
        handleClose={() => setConfirmOpen(false)}
        title={"Send an email to user or just settle ?"}
        open={confirmOpen}
        maxWidth="sm"
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <TextField
            label="UTR Number"
            variant="outlined"
            value={selectedItem.utr}
            onChange={(e) =>
              setSelectedItem((prev) => ({
                ...prev,
                utr: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Reference Number"
            variant="outlined"
            value={selectedItem.ref}
            onChange={(e) =>
              setSelectedItem((prev) => ({
                ...prev,
                ref: e.target.value,
              }))
            }
            fullWidth
          />
          <TextField
            label="Gross Amount (INR)"
            variant="outlined"
            value={selectedItem.gross_amt}
            onChange={(e) =>
              setSelectedItem((prev) => ({
                ...prev,
                gross_amt: e.target.value,
              }))
            }
            fullWidth
            type="number"
          />
          <TextField
            label="TDS Amount"
            variant="outlined"
            value={selectedItem.tds}
            onChange={(e) =>
              setSelectedItem((prev) => ({
                ...prev,
                tds: e.target.value,
              }))
            }
            fullWidth
            type="number"
          />
          <TextField
            label="Net Payable Amount"
            variant="outlined"
            value={selectedItem.amt}
            onChange={(e) =>
              setSelectedItem((prev) => ({
                ...prev,
                amt: e.target.value,
              }))
            }
            fullWidth
            type="number"
          />
        </div>

        <div
          className="aceept-reject-button"
          style={{ marginTop: "1.5rem", display: "flex", gap: "1rem" }}
        >
          <button className="accept" onClick={onClickSettleAndEmail}>
            Send Email
          </button>
          <button className="accept" onClick={onConfirmSettle}>
            Just Settle
          </button>
        </div>
      </CustomShowModal>
      </Grid>
    </>
  );
}

export default Payout;